// src/pages/TiktokCartPage.tsx
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "../config/api";

// ===== Types =====

type TiktokCart = {
  id: number;
  name: string;
  tiktok_basket_id: string | null;
  created_at: string;
  updated_at: string;
  stock_warning_count: number;
  last_reviewed_at: string | null;
};

type CartItem = {
  id: number;
  product_code: string;
  product_name: string;
  unit: string | null;
  tiktok_stock: number;
  stock_by_location: Record<string, number>;
  total_stock: number;
};

type CartsResponse = { ok?: boolean; items?: unknown; error?: string };
type CartResponse = { ok?: boolean; cart?: unknown; error?: string };
type CartCheckResponse = { ok?: boolean; cart?: unknown; items?: unknown; error?: string };
type ItemResponse = { ok?: boolean; item?: unknown; error?: string };
type TransferResponse = { ok?: boolean; error?: string };

type TransferDraft = {
  productCode: string;
  productName: string;
  unit: string | null;
  fromLocationCode: string;
  toLocationCode: string;
  qty: number;
  note: string;
  stockByLocation: Record<string, number>;
};

// ===== Validators =====

function toCart(row: unknown): TiktokCart | null {
  if (!row || typeof row !== "object") return null;
  const d = row as Record<string, unknown>;
  const id = Number(d.id ?? 0);
  const name = typeof d.name === "string" ? d.name.trim() : "";
  if (!id || !name) return null;
  return {
    id,
    name,
    tiktok_basket_id: typeof d.tiktok_basket_id === "string" && d.tiktok_basket_id.trim() ? d.tiktok_basket_id.trim() : null,
    created_at: typeof d.created_at === "string" ? d.created_at : "",
    updated_at: typeof d.updated_at === "string" ? d.updated_at : "",
    stock_warning_count: Number.isFinite(Number(d.stock_warning_count)) ? Number(d.stock_warning_count) : 0,
    last_reviewed_at: typeof d.last_reviewed_at === "string" ? d.last_reviewed_at : null,
  };
}

// PMN-CRITICAL(/TiktokCartPage): review threshold — carts not reviewed for > 6 days show red overdue badge.
function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.floor((Date.now() - t) / 86400000);
}

function reviewStatusLabel(cart: TiktokCart): { text: string; overdue: boolean } {
  const d = daysSince(cart.last_reviewed_at);
  if (d === null) return { text: "ยังไม่เคยเช็ค", overdue: true };
  if (d === 0) return { text: "เช็ควันนี้", overdue: false };
  if (d === 1) return { text: "เช็คเมื่อวาน", overdue: false };
  return { text: `เช็คเมื่อ ${d} วันก่อน`, overdue: d > 6 };
}

function toCartItem(row: unknown): CartItem | null {
  if (!row || typeof row !== "object") return null;
  const d = row as Record<string, unknown>;
  const id = Number(d.id ?? 0);
  const product_code = typeof d.product_code === "string" ? d.product_code.trim() : "";
  if (!id || !product_code) return null;

  const stockRaw =
    d.stock_by_location && typeof d.stock_by_location === "object"
      ? (d.stock_by_location as Record<string, unknown>)
      : {};
  const stock_by_location = Object.fromEntries(
    Object.entries(stockRaw).map(([k, v]) => [k, Number.isFinite(Number(v)) ? Number(v) : 0])
  );

  return {
    id,
    product_code,
    product_name: typeof d.product_name === "string" ? d.product_name.trim() : "",
    unit: typeof d.unit === "string" && d.unit.trim() ? d.unit.trim() : null,
    tiktok_stock: Number(d.tiktok_stock ?? 0),
    stock_by_location,
    total_stock: Number(d.total_stock ?? 0),
  };
}

function getFriendlyError(code: string): string {
  switch (code) {
    case "name_required": return "กรุณาใส่ชื่อรายการ";
    case "product_code_required": return "กรุณาใส่รหัสสินค้า";
    case "product_not_found": return "ไม่พบสินค้านี้ในระบบ";
    case "cart_not_found": return "ไม่พบรายการนี้";
    case "same_source_and_destination": return "กองต้นทางและปลายทางต้องไม่ใช่กองเดียวกัน";
    case "stock_not_enough": return "stock ต้นทางไม่พอสำหรับการย้าย";
    default: return code || "เกิดข้อผิดพลาด";
  }
}

function getSafeUnit(unit: string | null): string {
  if (!unit || !unit.trim()) return "ชิ้น";
  const t = unit.trim();
  if (/(เธ|เน|โ€|�)/.test(t) || /^[A-Za-z0-9\s._/-]+$/.test(t)) return "ชิ้น";
  if (/[฀-๿]/.test(t)) return t;
  return "ชิ้น";
}

const ALL_LOCATIONS = ["MAIN", "SHOP", "TIKTOK", "USA"];

function locationBadgeClass(code: string): string {
  switch (code) {
    case "MAIN": return "badge bg-primary";
    case "SHOP": return "badge bg-success";
    case "TIKTOK": return "badge bg-warning text-dark";
    case "USA": return "badge bg-danger";
    default: return "badge bg-secondary";
  }
}

function locationSelectStyle(code: string): React.CSSProperties {
  switch (code) {
    case "MAIN":   return { backgroundColor: "var(--bs-primary-bg-subtle)", borderColor: "var(--bs-primary-border-subtle)", color: "var(--bs-primary-text-emphasis)" };
    case "SHOP":   return { backgroundColor: "var(--bs-success-bg-subtle)", borderColor: "var(--bs-success-border-subtle)", color: "var(--bs-success-text-emphasis)" };
    case "TIKTOK": return { backgroundColor: "var(--bs-warning-bg-subtle)", borderColor: "var(--bs-warning-border-subtle)", color: "var(--bs-warning-text-emphasis)" };
    case "USA":    return { backgroundColor: "var(--bs-danger-bg-subtle)", borderColor: "var(--bs-danger-border-subtle)", color: "var(--bs-danger-text-emphasis)" };
    default:       return {};
  }
}

export default function TiktokCartPage(): JSX.Element {
  // Cart list
  const [carts, setCarts] = useState<TiktokCart[]>([]);
  const [cartsLoading, setCartsLoading] = useState(true);
  const [cartsError, setCartsError] = useState("");

  // Selected cart
  const [selectedCartId, setSelectedCartId] = useState<number | null>(null);
  const [checkItems, setCheckItems] = useState<CartItem[]>([]);
  const [checkCart, setCheckCart] = useState<TiktokCart | null>(null);
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkError, setCheckError] = useState("");

  // Create cart form
  const [createName, setCreateName] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");

  // Add item form
  const [newCode, setNewCode] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState("");
  const codeInputRef = useRef<HTMLInputElement>(null);

  // Delete cart
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Edit cart name
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editNameLoading, setEditNameLoading] = useState(false);
  const editNameRef = useRef<HTMLInputElement>(null);

  // Edit basket ID
  const [editingBasketId, setEditingBasketId] = useState<string | null>(null);
  const [editBasketIdLoading, setEditBasketIdLoading] = useState(false);
  const editBasketIdRef = useRef<HTMLInputElement>(null);

  // Toast
  const [toast, setToast] = useState<{ msg: string; type: "success" | "danger" | "warning" } | null>(null);

  // Transfer modal
  const [transfer, setTransfer] = useState<TransferDraft | null>(null);
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [transferError, setTransferError] = useState("");
  const [dragFromLoc, setDragFromLoc] = useState<string | null>(null);
  const [dragOverLoc, setDragOverLoc] = useState<string | null>(null);
  const qtyInputRef = useRef<HTMLInputElement>(null);

  // Auto-hide toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // Focus edit name input when entering edit mode
  useEffect(() => {
    if (editingName !== null) {
      setTimeout(() => editNameRef.current?.select(), 50);
    }
  }, [editingName !== null]);

  // Focus edit basket ID input when entering edit mode
  useEffect(() => {
    if (editingBasketId !== null) {
      setTimeout(() => editBasketIdRef.current?.select(), 50);
    }
  }, [editingBasketId !== null]);

  // ===== API =====

  async function loadCarts() {
    try {
      setCartsLoading(true);
      setCartsError("");
      const resp = await fetch(apiUrl("/tiktok-carts"), { headers: { Accept: "application/json" } });
      const data = (await resp.json().catch(() => ({}))) as CartsResponse;
      if (!resp.ok) throw new Error(data.error ?? "failed_to_fetch_tiktok_carts");
      setCarts(
        Array.isArray(data.items)
          ? data.items.map(toCart).filter((c): c is TiktokCart => c != null)
          : []
      );
    } catch (err) {
      setCartsError(err instanceof Error ? err.message : "โหลดรายการไม่สำเร็จ");
    } finally {
      setCartsLoading(false);
    }
  }

  // PMN-CRITICAL(/TiktokCartPage): stopPropagation prevents the button click from also selecting the cart (button is inside a <button> list-item).
  async function handleMarkReviewed(cartId: number, e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    try {
      const resp = await fetch(apiUrl(`/tiktok-carts/${cartId}/mark-reviewed`), { method: "POST" });
      if (!resp.ok) throw new Error("failed_to_mark_reviewed");
      await loadCarts();
    } catch (err) {
      setCartsError(err instanceof Error ? err.message : "บันทึกไม่สำเร็จ");
    }
  }

  // PMN-CRITICAL(/TiktokCartPage): Check data compares cart items against real inventory by active location code, including TIKTOK stock.
  async function loadCheck(cartId: number) {
    try {
      setCheckLoading(true);
      setCheckError("");
      const resp = await fetch(apiUrl(`/tiktok-carts/${cartId}/check`), {
        headers: { Accept: "application/json" },
      });
      const data = (await resp.json().catch(() => ({}))) as CartCheckResponse;
      if (!resp.ok) throw new Error(data.error ?? "failed_to_check_tiktok_cart");
      setCheckCart(toCart(data.cart));
      setCheckItems(
        Array.isArray(data.items)
          ? data.items.map(toCartItem).filter((i): i is CartItem => i != null)
          : []
      );
    } catch (err) {
      setCheckError(err instanceof Error ? err.message : "โหลดข้อมูลไม่สำเร็จ");
    } finally {
      setCheckLoading(false);
    }
  }

  useEffect(() => { void loadCarts(); }, []);

  useEffect(() => {
    if (selectedCartId != null) {
      setCheckItems([]);
      setCheckCart(null);
      setAddError("");
      setNewCode("");
      void loadCheck(selectedCartId);
    }
  }, [selectedCartId]);

  async function handleCreateCart(e: React.FormEvent) {
    e.preventDefault();
    const name = createName.trim();
    if (!name) return;
    try {
      setCreateLoading(true);
      setCreateError("");
      const resp = await fetch(apiUrl("/tiktok-carts"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ name }),
      });
      const data = (await resp.json().catch(() => ({}))) as CartResponse;
      if (!resp.ok || !data.ok) throw new Error(data.error ?? "");
      const cart = toCart(data.cart);
      if (cart) {
        setCarts((prev) => [cart, ...prev]);
        setSelectedCartId(cart.id);
      }
      setCreateName("");
    } catch (err) {
      setCreateError(getFriendlyError(err instanceof Error ? err.message : ""));
    } finally {
      setCreateLoading(false);
    }
  }

  async function handleDeleteCart() {
    if (!selectedCartId) return;
    const cart = carts.find((c) => c.id === selectedCartId);
    if (!window.confirm(`ลบรายการ "${cart?.name ?? ""}" ?\nสินค้าทั้งหมดในรายการจะถูกลบด้วย`)) return;
    try {
      setDeleteLoading(true);
      const resp = await fetch(apiUrl(`/tiktok-carts/${selectedCartId}`), {
        method: "DELETE",
        headers: { Accept: "application/json" },
      });
      const data = (await resp.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!resp.ok || !data.ok) throw new Error(data.error ?? "");
      setCarts((prev) => prev.filter((c) => c.id !== selectedCartId));
      setSelectedCartId(null);
      setCheckCart(null);
      setCheckItems([]);
      setToast({ msg: `ลบ "${cart?.name ?? ""}" แล้ว`, type: "success" });
    } catch (err) {
      setToast({ msg: err instanceof Error ? err.message : "ลบไม่สำเร็จ", type: "danger" });
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleSaveBasketId(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedCartId || editingBasketId === null) return;
    try {
      setEditBasketIdLoading(true);
      const resp = await fetch(apiUrl(`/tiktok-carts/${selectedCartId}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ tiktok_basket_id: editingBasketId.trim() || null }),
      });
      const data = (await resp.json().catch(() => ({}))) as CartResponse;
      if (!resp.ok || !data.ok) throw new Error(data.error ?? "");
      const updated = toCart(data.cart);
      if (updated) {
        setCarts((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
        setCheckCart(updated);
      }
      setEditingBasketId(null);
    } catch (err) {
      setToast({ msg: getFriendlyError(err instanceof Error ? err.message : ""), type: "danger" });
    } finally {
      setEditBasketIdLoading(false);
    }
  }

  async function handleRenameCart(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedCartId || editingName === null) return;
    const name = editingName.trim();
    if (!name) return;
    try {
      setEditNameLoading(true);
      const resp = await fetch(apiUrl(`/tiktok-carts/${selectedCartId}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ name }),
      });
      const data = (await resp.json().catch(() => ({}))) as CartResponse;
      if (!resp.ok || !data.ok) throw new Error(data.error ?? "");
      const updated = toCart(data.cart);
      if (updated) {
        setCarts((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
        setCheckCart(updated);
      }
      setEditingName(null);
    } catch (err) {
      setToast({ msg: getFriendlyError(err instanceof Error ? err.message : ""), type: "danger" });
    } finally {
      setEditNameLoading(false);
    }
  }

  // PMN-CRITICAL(/TiktokCartPage): Adding duplicate product resets qty to 1 in backend upsert; this page is a checklist, not a quantity cart.
  async function handleAddItem(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedCartId) return;
    const code = newCode.trim();
    if (!code) return;
    try {
      setAddLoading(true);
      setAddError("");
      const resp = await fetch(apiUrl(`/tiktok-carts/${selectedCartId}/items`), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ product_code: code, qty: 1 }),
      });
      const data = (await resp.json().catch(() => ({}))) as ItemResponse;
      if (!resp.ok || !data.ok) throw new Error(data.error ?? "");
      setNewCode("");
      codeInputRef.current?.focus();
      await loadCheck(selectedCartId);
      void loadCarts();
    } catch (err) {
      setAddError(getFriendlyError(err instanceof Error ? err.message : ""));
    } finally {
      setAddLoading(false);
    }
  }

  async function handleRemoveItem(itemId: number, productName?: string) {
    if (!selectedCartId) return;
    if (!window.confirm(`ลบ "${productName ?? ""}" ออกจากรายการ?`)) return;
    try {
      const resp = await fetch(apiUrl(`/tiktok-carts/${selectedCartId}/items/${itemId}`), {
        method: "DELETE",
        headers: { Accept: "application/json" },
      });
      const data = (await resp.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!resp.ok || !data.ok) throw new Error(data.error ?? "");
      await loadCheck(selectedCartId);
    } catch (err) {
      setToast({ msg: err instanceof Error ? err.message : "ลบสินค้าไม่สำเร็จ", type: "danger" });
    }
  }

  function openTransfer(item: CartItem) {
    // Default from = location with highest stock (not TIKTOK), to = TIKTOK
    const fromCode =
      ["MAIN", "SHOP", "USA"].reduce((best, loc) => {
        return (item.stock_by_location[loc] ?? 0) > (item.stock_by_location[best] ?? 0)
          ? loc
          : best;
      }, "MAIN");

    setTransferError("");
    setTransfer({
      productCode: item.product_code,
      productName: item.product_name,
      unit: item.unit,
      fromLocationCode: fromCode,
      toLocationCode: "TIKTOK",
      qty: 1,
      note: "",
      stockByLocation: item.stock_by_location,
    });
  }

  // PMN-CRITICAL(/TiktokCartPage): Transfer submits to /stock-transfers and moves real stock, then reloads the cart stock check.
  async function handleSubmitTransfer(e: React.FormEvent) {
    e.preventDefault();
    if (!transfer || !selectedCartId) return;
    setTransferError("");
    try {
      setTransferSubmitting(true);
      const resp = await fetch(apiUrl("/stock-transfers"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          product_code: transfer.productCode,
          from_location_code: transfer.fromLocationCode,
          to_location_code: transfer.toLocationCode,
          qty: transfer.qty,
          note: transfer.note.trim() || undefined,
        }),
      });
      const data = (await resp.json().catch(() => ({}))) as TransferResponse;
      if (!resp.ok || !data.ok) throw new Error(data.error ?? "");
      setToast({
        msg: `ย้าย ${transfer.productCode} จำนวน ${transfer.qty} ${getSafeUnit(transfer.unit)} จาก ${transfer.fromLocationCode} → ${transfer.toLocationCode} เรียบร้อย`,
        type: "success",
      });
      setTransfer(null);
      await loadCheck(selectedCartId);
    } catch (err) {
      setTransferError(getFriendlyError(err instanceof Error ? err.message : ""));
    } finally {
      setTransferSubmitting(false);
    }
  }

  const fromStock = transfer
    ? Number(transfer.stockByLocation[transfer.fromLocationCode] ?? 0)
    : 0;

  // ===== Render =====

  return (
    <div className="min-vh-100 bg-light">
      {/* Toast */}
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
        {toast && (
          <div className={`alert alert-${toast.type} shadow mb-0 rounded-3 py-2 px-3`}>
            {toast.msg}
          </div>
        )}
      </div>

      <div className="container-fluid py-3 py-md-4" style={{ maxWidth: 1200 }}>
        {/* Header */}
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
          <div>
            <h4 className="mb-0 text-primary">TikTok Cart</h4>
            <div className="text-muted small">ดู stock TIKTOK เทียบกับ stock รวมทุกกอง</div>
          </div>
          <Link to="/" className="btn btn-outline-primary btn-sm">กลับหน้าเมนู</Link>
        </div>

        <div className="row g-3">
          {/* ===== LEFT: List ===== */}
          <div className="col-12 col-md-4 col-lg-3">
            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-3">
                <div className="fw-semibold mb-2">รายการที่บันทึกไว้</div>

                <form className="d-flex gap-1 mb-3" onSubmit={handleCreateCart}>
                  <input
                    className="form-control form-control-sm"
                    placeholder="ชื่อรายการใหม่..."
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    maxLength={200}
                  />
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm flex-shrink-0"
                    disabled={createLoading || !createName.trim()}
                  >
                    {createLoading ? "..." : "สร้าง"}
                  </button>
                </form>
                {createError && (
                  <div className="alert alert-warning py-1 px-2 small mb-2">{createError}</div>
                )}

                {cartsLoading && (
                  <div className="text-center text-muted py-3 small">
                    <div className="spinner-border spinner-border-sm me-1" role="status" />
                    กำลังโหลด...
                  </div>
                )}
                {cartsError && (
                  <div className="alert alert-warning py-1 px-2 small">{cartsError}</div>
                )}

                <div className="list-group list-group-flush rounded-3" style={{ maxHeight: 480, overflowY: "auto" }}>
                  {!cartsLoading && carts.length === 0 && (
                    <div className="text-center text-muted py-3 small">ยังไม่มีรายการ</div>
                  )}
                  {carts.map((cart) => (
                    <button
                      key={cart.id}
                      type="button"
                      className={`list-group-item list-group-item-action border-0 rounded-3 mb-1 position-relative ${
                        selectedCartId === cart.id ? "active" : ""
                      }`}
                      onClick={() => setSelectedCartId(cart.id)}
                    >
                      {/* PMN-CRITICAL(/TiktokCartPage): warning badge shows when cart has items where tiktok_stock > 0 AND tiktok_stock === total_stock (TIKTOK is only source). */}
                      {cart.stock_warning_count > 0 && (
                        <span
                          className="position-absolute badge rounded-pill bg-warning text-dark shadow-sm"
                          style={{ top: "6px", right: "6px", fontSize: "0.7rem", zIndex: 1 }}
                          title={`⚠ ${cart.stock_warning_count} รายการที่ TIKTOK เป็นแหล่งเดียว — ขายแล้วไม่มีของย้ายมาเพิ่ม`}
                        >
                          ⚠ {cart.stock_warning_count}
                        </span>
                      )}
                      <div className="fw-semibold small" style={{ paddingRight: cart.stock_warning_count > 0 ? "3rem" : undefined }}>
                        <span className="badge bg-secondary me-1" style={{ fontSize: "0.65rem", fontWeight: 400 }}>#{cart.id}</span>
                        {cart.name}
                      </div>
                      {cart.tiktok_basket_id && (
                        <div className="small text-muted" style={{ fontSize: "0.72rem" }}>
                          🛒 {cart.tiktok_basket_id}
                        </div>
                      )}
                      <div className="small" style={{ opacity: 0.65, fontSize: "0.72rem" }}>
                        {cart.updated_at ? new Date(cart.updated_at).toLocaleString("th-TH") : "-"}
                      </div>
                      {/* PMN-CRITICAL(/TiktokCartPage): weekly review button — stopPropagation กันไม่ให้กดปุ่มแล้ว switch cart */}
                      {(() => {
                        const s = reviewStatusLabel(cart);
                        const isSelected = selectedCartId === cart.id;
                        // PMN-CRITICAL(/TiktokCartPage): button + status text ต้องเห็นทั้งบน active(น้ำเงิน) และ default(ขาว) — เลือก variant ตาม isSelected
                        const btnCls = isSelected ? "btn btn-light btn-sm py-0 px-2 fw-semibold" : "btn btn-outline-primary btn-sm py-0 px-2";
                        const statusCls = isSelected
                          ? (s.overdue ? "small text-warning fw-bold" : "small text-white-50")
                          : (s.overdue ? "small text-danger fw-semibold" : "small text-muted");
                        return (
                          <div className="d-flex align-items-center gap-2 mt-1">
                            <button
                              type="button"
                              className={btnCls}
                              style={{ fontSize: "0.7rem" }}
                              onClick={(e) => void handleMarkReviewed(cart.id, e)}
                              title="บันทึกว่าเช็คตะกร้านี้แล้ว"
                            >
                              ✓ เช็ค
                            </button>
                            <span className={statusCls} style={{ fontSize: "0.7rem" }}>
                              {s.overdue && "⚠ "}{s.text}
                            </span>
                          </div>
                        );
                      })()}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ===== RIGHT: Detail ===== */}
          <div className="col-12 col-md-8 col-lg-9">
            {!selectedCartId ? (
              <div className="card border-0 shadow-sm rounded-4">
                <div className="card-body text-center text-muted py-5">
                  เลือกรายการทางซ้าย หรือสร้างรายการใหม่
                </div>
              </div>
            ) : (
              <div className="card border-0 shadow-sm rounded-4">
                <div className="card-body p-3 p-md-4">
                  {/* Header */}
                  <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      {editingName !== null ? (
                        <form className="d-flex gap-1 align-items-center" onSubmit={handleRenameCart}>
                          <input
                            ref={editNameRef}
                            className="form-control form-control-sm"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            maxLength={200}
                            style={{ maxWidth: 260 }}
                          />
                          <button
                            type="submit"
                            className="btn btn-primary btn-sm flex-shrink-0"
                            disabled={editNameLoading || !editingName.trim()}
                          >
                            {editNameLoading ? "..." : "บันทึก"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm flex-shrink-0"
                            onClick={() => setEditingName(null)}
                            disabled={editNameLoading}
                          >
                            ยกเลิก
                          </button>
                        </form>
                      ) : (
                        <div className="d-flex align-items-center gap-2">
                          <h5 className="mb-0">{checkCart?.name ?? "..."}</h5>
                          {checkCart && (
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-muted"
                              title="แก้ไขชื่อ"
                              onClick={() => setEditingName(checkCart.name)}
                            >
                              ✎
                            </button>
                          )}
                        </div>
                      )}
                      {checkCart && editingName === null && (
                        <>
                          {/* Basket ID row */}
                          <div className="d-flex align-items-center gap-1 mt-1">
                            <span className="small text-muted" style={{ whiteSpace: "nowrap" }}>TikTok Basket ID:</span>
                            {editingBasketId !== null ? (
                              <form className="d-flex gap-1 align-items-center" onSubmit={handleSaveBasketId}>
                                <input
                                  ref={editBasketIdRef}
                                  className="form-control form-control-sm"
                                  value={editingBasketId}
                                  onChange={(e) => setEditingBasketId(e.target.value)}
                                  maxLength={200}
                                  placeholder="ใส่ ID ตะกร้า TikTok"
                                  style={{ maxWidth: 200 }}
                                />
                                <button type="submit" className="btn btn-primary btn-sm flex-shrink-0" disabled={editBasketIdLoading}>
                                  {editBasketIdLoading ? "..." : "บันทึก"}
                                </button>
                                <button type="button" className="btn btn-outline-secondary btn-sm flex-shrink-0" onClick={() => setEditingBasketId(null)} disabled={editBasketIdLoading}>
                                  ยกเลิก
                                </button>
                              </form>
                            ) : (
                              <>
                                <span className="small fw-semibold">
                                  {checkCart.tiktok_basket_id ?? <span className="text-muted fst-italic">ยังไม่ได้ใส่</span>}
                                </span>
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm p-0 text-muted"
                                  title="แก้ไข Basket ID"
                                  onClick={() => setEditingBasketId(checkCart.tiktok_basket_id ?? "")}
                                >
                                  ✎
                                </button>
                              </>
                            )}
                          </div>
                          <div className="small text-muted mt-1">
                            อัปเดต: {new Date(checkCart.updated_at).toLocaleString("th-TH")}
                          </div>
                          {(() => {
                            const s = reviewStatusLabel(checkCart);
                            return (
                              <div className={`small mt-1 ${s.overdue ? "text-danger fw-semibold" : "text-muted"}`}>
                                {s.overdue && "⚠ "}เช็คล่าสุด: {s.text}
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </div>
                    <div className="d-flex gap-2 flex-shrink-0">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => void loadCheck(selectedCartId)}
                        disabled={checkLoading}
                      >
                        รีเฟรช
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => void handleDeleteCart()}
                        disabled={deleteLoading}
                      >
                        ลบรายการ
                      </button>
                    </div>
                  </div>

                  {/* Add item */}
                  <form className="d-flex gap-2 mb-3" onSubmit={handleAddItem}>
                    <input
                      ref={codeInputRef}
                      className="form-control form-control-sm"
                      placeholder="พิมพ์รหัสสินค้า แล้วกด Enter"
                      value={newCode}
                      onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                      style={{ maxWidth: 260 }}
                      autoComplete="off"
                    />
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm flex-shrink-0"
                      disabled={addLoading || !newCode.trim()}
                    >
                      {addLoading ? "..." : "เพิ่ม"}
                    </button>
                  </form>
                  {addError && (
                    <div className="alert alert-warning py-1 px-2 small mb-2">{addError}</div>
                  )}

                  {checkError && <div className="alert alert-warning rounded-3">{checkError}</div>}
                  {checkLoading && (
                    <div className="text-center text-muted py-4">
                      <div className="spinner-border spinner-border-sm me-2" role="status" />
                      กำลังโหลด...
                    </div>
                  )}

                  {!checkLoading && checkItems.length === 0 && !checkError && (
                    <div className="text-center text-muted py-4 small">
                      ยังไม่มีสินค้าในรายการ — พิมพ์รหัสสินค้าด้านบนเพื่อเพิ่ม
                    </div>
                  )}

                  {/* Table */}
                  {checkItems.length > 0 && (
                    <div className="table-responsive rounded-3 border">
                      <table className="table table-hover align-middle mb-0" style={{ minWidth: 500 }}>
                        <thead className="table-light sticky-top">
                          <tr>
                            <th>สินค้า</th>
                            {ALL_LOCATIONS.map((loc) => (
                              <th key={loc} className="text-center" style={{ width: 80 }}>
                                <span className={locationBadgeClass(loc)}>{loc}</span>
                              </th>
                            ))}
                            <th className="text-center" style={{ width: 70 }}>รวม</th>
                            <th style={{ width: 100 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {checkItems.map((item) => (
                            <tr key={item.id}>
                              <td>
                                <div className="small text-muted lh-1">{item.product_code}</div>
                                <div className="fw-semibold" style={{ fontSize: "0.875rem" }}>
                                  {item.product_name || item.product_code}
                                </div>
                              </td>
                              {ALL_LOCATIONS.map((loc) => {
                                const qty = Number(item.stock_by_location[loc] ?? 0);
                                return (
                                  <td key={loc} className="text-center">
                                    {qty === 0 ? (
                                      <span className="text-muted small">-</span>
                                    ) : (
                                      <span className={locationBadgeClass(loc)}>
                                        {qty.toLocaleString()}
                                      </span>
                                    )}
                                  </td>
                                );
                              })}
                              <td className="text-center fw-semibold small">
                                {item.total_stock.toLocaleString()}
                              </td>
                              <td>
                                <div className="d-flex gap-1 justify-content-end">
                                  <button
                                    type="button"
                                    className="btn btn-warning btn-sm"
                                    onClick={() => openTransfer(item)}
                                  >
                                    ย้าย
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => void handleRemoveItem(item.id, item.product_name)}
                                    title="ลบออกจากรายการ"
                                  >
                                    ✕
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== Transfer Modal ===== */}
      {transfer && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
          onClick={(e) => { if (e.target === e.currentTarget) setTransfer(null); }}
        >
          <div className="card border-0 shadow-lg rounded-4 mx-3" style={{ width: "100%", maxWidth: 420 }}>
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">ย้าย Stock</h5>
                <button type="button" className="btn-close" onClick={() => setTransfer(null)} />
              </div>

              <div className="mb-3">
                <div className="small text-muted">{transfer.productCode}</div>
                <div className="fw-semibold">{transfer.productName}</div>
              </div>

              {/* Stock summary */}
              <div className="d-flex gap-2 mb-3">
                {ALL_LOCATIONS.map((loc) => {
                  const stockQty = Number(transfer.stockByLocation[loc] ?? 0);
                  const isDraggable = stockQty > 0;
                  const isDragging = dragFromLoc === loc;
                  const isDropTarget = dragOverLoc === loc && dragFromLoc !== loc;
                  const baseBg =
                    loc === "MAIN"
                      ? "border-primary-subtle bg-primary-subtle"
                      : loc === "SHOP"
                      ? "border-success-subtle bg-success-subtle"
                      : "border-warning-subtle bg-warning-subtle";
                  return (
                    <div
                      key={loc}
                      draggable={isDraggable}
                      onDragStart={() => { if (isDraggable) setDragFromLoc(loc); }}
                      onDragEnd={() => { setDragFromLoc(null); setDragOverLoc(null); }}
                      onDragOver={(e) => { if (dragFromLoc && dragFromLoc !== loc) { e.preventDefault(); setDragOverLoc(loc); } }}
                      onDragLeave={() => setDragOverLoc(null)}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (!dragFromLoc || dragFromLoc === loc) return;
                        setTransfer((prev) => prev ? {
                          ...prev,
                          fromLocationCode: dragFromLoc,
                          toLocationCode: loc,
                          qty: 1,
                        } : prev);
                        setDragFromLoc(null);
                        setDragOverLoc(null);
                        setTimeout(() => qtyInputRef.current?.focus(), 50);
                      }}
                      className={`flex-fill text-center rounded-3 py-2 border ${
                        isDropTarget ? "border-warning border-3 shadow " + baseBg : baseBg
                      }`}
                      style={{
                        cursor: isDraggable ? "grab" : "default",
                        opacity: isDragging ? 0.45 : isDraggable ? 1 : 0.35,
                        transition: "opacity 0.15s, box-shadow 0.15s",
                      }}
                    >
                      <div className="small text-muted">{loc}</div>
                      <div className="fw-bold">{stockQty}</div>
                      {isDraggable && (
                        <div style={{ fontSize: 10, color: "#888", lineHeight: 1 }}>⠿ ลาก</div>
                      )}
                    </div>
                  );
                })}
              </div>

              <form onSubmit={handleSubmitTransfer}>
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small mb-1 fw-semibold">จาก</label>
                    <select
                      className="form-select form-select-sm"
                      style={locationSelectStyle(transfer.fromLocationCode)}
                      value={transfer.fromLocationCode}
                      onChange={(e) => {
                        const newFrom = e.target.value;
                        setTransfer((prev) => {
                          if (!prev) return prev;
                          const newTo = prev.toLocationCode === newFrom
                            ? ALL_LOCATIONS.find((l) => l !== newFrom) ?? "TIKTOK"
                            : prev.toLocationCode;
                          return { ...prev, fromLocationCode: newFrom, toLocationCode: newTo, qty: 1 };
                        });
                      }}
                    >
                      {ALL_LOCATIONS.map((loc) => (
                        <option key={loc} value={loc}>
                          {loc} ({Number(transfer.stockByLocation[loc] ?? 0)})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label small mb-1 fw-semibold">ไป</label>
                    <select
                      className="form-select form-select-sm"
                      style={locationSelectStyle(transfer.toLocationCode)}
                      value={transfer.toLocationCode}
                      onChange={(e) =>
                        setTransfer((prev) => prev ? { ...prev, toLocationCode: e.target.value } : prev)
                      }
                    >
                      {ALL_LOCATIONS.filter((l) => l !== transfer.fromLocationCode).map((loc) => (
                        <option key={loc} value={loc}>{loc}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label small mb-1 fw-semibold">
                    จำนวน{" "}
                    <span className="text-muted fw-normal">
                      (stock ต้นทาง: {fromStock.toLocaleString()} {getSafeUnit(transfer.unit)})
                    </span>
                  </label>
                  <input
                    ref={qtyInputRef}
                    type="number"
                    min={0}
                    max={fromStock}
                    step={1}
                    className="form-control form-control-sm"
                    value={transfer.qty}
                    onChange={(e) =>
                      setTransfer((prev) =>
                        prev ? { ...prev, qty: Number(e.target.value) } : prev
                      )
                    }
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label small mb-1 fw-semibold">หมายเหตุ</label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="เช่น เติม stock TikTok"
                    value={transfer.note}
                    onChange={(e) =>
                      setTransfer((prev) => (prev ? { ...prev, note: e.target.value } : prev))
                    }
                  />
                </div>

                {transferError && (
                  <div className="alert alert-warning py-2 px-3 small mb-3">{transferError}</div>
                )}

                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-outline-secondary flex-fill" onClick={() => setTransfer(null)}>
                    ยกเลิก
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary flex-fill"
                    disabled={transferSubmitting || fromStock <= 0}
                  >
                    {transferSubmitting ? "กำลังย้าย..." : "ยืนยันย้าย"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
